import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Fab,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Square as SquareIcon,
  Euro as EuroIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Apartment as ApartmentIcon,
  Room as RoomIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled Components für Triton Design
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  '& .MuiTableHead-root': {
    backgroundColor: '#f7fafc',
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    color: '#1a365d',
    borderBottom: '2px solid #e2e8f0',
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  margin: theme.spacing(0, 0.5),
  '&.edit': {
    color: '#1a365d',
    '&:hover': {
      backgroundColor: 'rgba(26, 54, 93, 0.1)',
    },
  },
  '&.delete': {
    color: '#e53e3e',
    '&:hover': {
      backgroundColor: 'rgba(229, 62, 62, 0.1)',
    },
  },
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
  color: '#ffffff',
  '&:hover': {
    background: 'linear-gradient(135deg, #2d3748 0%, #1a365d 100%)',
    transform: 'scale(1.05)',
  },
}));

const ObjectChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(26, 54, 93, 0.1)',
  color: '#1a365d',
  border: '1px solid rgba(26, 54, 93, 0.2)',
  fontWeight: 600,
  '& .MuiChip-icon': {
    color: '#1a365d',
  },
}));

const UnitTypeChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(214, 158, 46, 0.1)',
  color: '#d69e2e',
  border: '1px solid rgba(214, 158, 46, 0.2)',
  fontWeight: 500,
}));

const Wohneinheiten = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [objects, setObjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  
  // Formular-Daten
  const [formData, setFormData] = useState({
    objektid: '',
    wohneinheitnummer: '',
    wohneinheitbezeichnung: '',
    wohneinheitgroesse: '',
    wohneinheittyp: '',
    wohneinheitstatus: 'verfügbar',
    lage_im_objekt: '',
    wohnungsbezeichnung: '',
  });

  // URL-Parameter prüfen (für "Neue Wohneinheit" Link)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('action') === 'new') {
      handleOpenDialog();
      navigate('/dashboard/wohneinheiten', { replace: true });
    }
  }, [location.search, navigate]);

  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/api/wohneinheiten');
      setUnits(data);
    } catch (err) {
      console.error('Fehler beim Laden der Wohneinheiten:', err);
      showSnackbar('Fehler beim Laden der Wohneinheiten', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const fetchObjects = useCallback(async () => {
    try {
      const data = await apiService.get('/api/objects');
      setObjects(data);
    } catch (err) {
      console.error('Fehler beim Laden der Objekte:', err);
      showSnackbar('Fehler beim Laden der Objekte', 'error');
    }
  }, [showSnackbar]);

  // Wohneinheiten und Objekte laden
  useEffect(() => {
    fetchUnits();
    fetchObjects();
  }, [fetchUnits, fetchObjects]);

  // Preis formatieren
  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  // Objekt-Name anhand ID finden
  const getObjectName = (objektId) => {
    const object = objects.find(obj => obj.objektid === objektId);
    return object ? object.objektkuerzel : 'Unbekannt';
  };

  // Objekt-Adresse anhand ID finden
  const getObjectAddress = (objektId) => {
    const object = objects.find(obj => obj.objektid === objektId);
    return object ? `${object.objektstrasse} ${object.objekthausnummer}` : '';
  };

  // Status-Farbe bestimmen
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'vermietet': return '#38a169';
      case 'verfügbar': return '#d69e2e';
      case 'ausgelaufen': return '#e53e3e';
      case 'renovierung': return '#805ad5';
      default: return '#718096';
    }
  };

  // Dialog öffnen (Neu oder Bearbeiten)
  const handleOpenDialog = (unit = null) => {
    if (unit) {
      setEditMode(true);
      setSelectedUnit(unit);
      setFormData({
        objektid: unit.objektid || '',
        wohneinheitnummer: unit.wohneinheitnummer || '',
        wohneinheitbezeichnung: unit.wohneinheitbezeichnung || '',
        wohneinheitgroesse: unit.wohneinheitgroesse || '',
        wohneinheittyp: unit.wohneinheittyp || '',
        wohneinheitstatus: unit.wohneinheitstatus || 'verfügbar',
        lage_im_objekt: unit.lage_im_objekt || '',
        wohnungsbezeichnung: unit.wohnungsbezeichnung_detail || '',
      });
      setSelectedUnit(null);
      setFormData({
        objektid: '',
        wohneinheitnummer: '',
        wohneinheitbezeichnung: '',
        wohneinheitgroesse: '',
        wohneinheittyp: '',
        wohneinheitstatus: 'verfügbar',
        lage_im_objekt: '',
      });
    }
    setOpen(true);
  };

  // Dialog schließen
  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setSelectedUnit(null);
    setFormData({
      objektid: '',
      wohneinheitnummer: '',
      wohneinheitbezeichnung: '',
      wohneinheitgroesse: '',
      wohneinheittyp: '',
      wohneinheitstatus: 'verfügbar',
      lage_im_objekt: '',
    });
  };

  // Formular-Handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Wohneinheit speichern
  const handleSaveUnit = async () => {
    try {
      setLoading(true);
      
      if (editMode && selectedUnit) {
        // Wohneinheit aktualisieren
        await apiService.put(`/api/wohneinheiten/${selectedUnit.wohneinheitid}`, formData);
        showSnackbar('Wohneinheit erfolgreich aktualisiert', 'success');
      } else {
        // Neue Wohneinheit erstellen
        await apiService.post('/api/wohneinheiten', formData);
        showSnackbar('Wohneinheit erfolgreich erstellt', 'success');
      }
      
      handleCloseDialog();
      fetchUnits(); // Liste neu laden
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      showSnackbar('Fehler beim Speichern der Wohneinheit', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Wohneinheit löschen
  const handleDeleteUnit = async (unitId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diese Wohneinheit löschen möchten?')) {
      try {
        await apiService.delete(`/api/wohneinheiten/${unitId}`);
        showSnackbar('Wohneinheit erfolgreich gelöscht', 'success');
        fetchUnits();
      } catch (err) {
        console.error('Fehler beim Löschen:', err);
        showSnackbar('Fehler beim Löschen der Wohneinheit', 'error');
      }
    }
  };

  // Statistiken berechnen
  const totalUnits = units.length;
  const rentedUnits = units.filter(unit => unit.wohneinheitstatus?.toLowerCase() === 'vermietet').length;
  const availableUnits = units.filter(unit => unit.wohneinheitstatus?.toLowerCase() === 'verfügbar').length;
  const expiredUnits = units.filter(unit => unit.wohneinheitstatus?.toLowerCase() === 'ausgelaufen').length;
  const totalRent = units.reduce((sum, unit) => sum + (parseFloat(unit.wohneinheitgesamtmiete) || 0), 0);

  // Mieter-Namen formatieren
  const formatMieterName = (unit) => {
    if (!unit.mieter1_vorname && !unit.mieter1_nachname) return '-';
    
    let mieterName = `${unit.mieter1_vorname || ''} ${unit.mieter1_nachname || ''}`.trim();
    
    if (unit.mieter2_vorname || unit.mieter2_nachname) {
      const mieter2 = `${unit.mieter2_vorname || ''} ${unit.mieter2_nachname || ''}`.trim();
      if (mieter2) {
        mieterName += ` & ${mieter2}`;
      }
    }
    
    return mieterName;
  };

  // Datum formatieren
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  return (
    <Container maxWidth="xl">
      <Box my={4}>
        {/* Header */}
        <StyledCard>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <ApartmentIcon sx={{ fontSize: 32, color: '#1a365d', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ color: '#1a365d', fontWeight: 600 }}>
                  Wohneinheiten-Verwaltung
                </Typography>
                <Typography variant="body1" sx={{ color: '#718096' }}>
                  Verwalten Sie alle Wohneinheiten in Ihren Immobilienobjekten
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={2.4}>
                <Box display="flex" alignItems="center">
                  <HomeIcon sx={{ color: '#d69e2e', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {totalUnits}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#718096' }}>
                      Wohneinheiten gesamt
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box display="flex" alignItems="center">
                  <RoomIcon sx={{ color: '#38a169', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {rentedUnits}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#718096' }}>
                      Vermietet
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box display="flex" alignItems="center">
                  <SquareIcon sx={{ color: '#d69e2e', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {availableUnits}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#718096' }}>
                      Verfügbar
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box display="flex" alignItems="center">
                  <SquareIcon sx={{ color: '#e53e3e', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {expiredUnits}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#718096' }}>
                      Ausgelaufen
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box display="flex" alignItems="center">
                  <EuroIcon sx={{ color: '#38a169', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatPrice(totalRent)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#718096' }}>
                      Mieteinnahmen gesamt
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        {/* Wohneinheitenliste */}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Objekt</TableCell>
                <TableCell>Nummer</TableCell>
                <TableCell>Bezeichnung</TableCell>
                <TableCell>Größe (m²)</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Mieter</TableCell>
                <TableCell>Miete</TableCell>
                <TableCell>Mietvertrag</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {units.map((unit) => (
                <TableRow key={unit.wohneinheitid} hover>
                  <TableCell>
                    <Box>
                      <ObjectChip 
                        icon={<BusinessIcon />}
                        label={getObjectName(unit.objektid)}
                        size="small"
                      />
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#718096' }}>
                        {getObjectAddress(unit.objektid)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {unit.wohneinheitnummer}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {unit.wohneinheitbezeichnung || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <SquareIcon sx={{ fontSize: 16, color: '#718096', mr: 1 }} />
                      <Typography variant="body2">
                        {unit.wohneinheitgroesse ? `${unit.wohneinheitgroesse} m²` : '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {unit.wohneinheittyp && (
                      <UnitTypeChip 
                        label={unit.wohneinheittyp}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatMieterName(unit)}
                    </Typography>
                    {unit.mietvertrag_start && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#718096' }}>
                        seit {formatDate(unit.mietvertrag_start)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#38a169' }}>
                      {formatPrice(unit.wohneinheitgesamtmiete)}
                    </Typography>
                    {unit.wohneinheitmiete && unit.wohneinheitnebenkosten && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#718096' }}>
                        {formatPrice(unit.wohneinheitmiete)} + {formatPrice(unit.wohneinheitnebenkosten)} NK
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {unit.mietvertragid ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a365d' }}>
                          ID: {unit.mietvertragid}
                        </Typography>
                        {unit.mietvertrag_ende && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#718096' }}>
                            bis {formatDate(unit.mietvertrag_ende)}
                          </Typography>
                        )}
                        {!unit.mietvertrag_ende && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#38a169' }}>
                            unbefristet
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Kein Mietvertrag
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={unit.wohneinheitstatus || 'Unbekannt'}
                      size="small"
                      sx={{
                        backgroundColor: `${getStatusColor(unit.wohneinheitstatus)}20`,
                        color: getStatusColor(unit.wohneinheitstatus),
                        border: `1px solid ${getStatusColor(unit.wohneinheitstatus)}40`,
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <ActionButton 
                      className="edit"
                      size="small"
                      onClick={() => handleOpenDialog(unit)}
                      title="Bearbeiten"
                    >
                      <EditIcon fontSize="small" />
                    </ActionButton>
                    <ActionButton 
                      className="delete"
                      size="small"
                      onClick={() => handleDeleteUnit(unit.wohneinheitid)}
                      title="Löschen"
                    >
                      <DeleteIcon fontSize="small" />
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {units.length === 0 && !loading && (
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                Keine Wohneinheiten vorhanden. Klicken Sie auf das + Symbol, um eine neue Wohneinheit anzulegen.
              </Typography>
            </Box>
          )}
        </StyledTableContainer>

        {/* Floating Action Button */}
        <StyledFab 
          onClick={() => handleOpenDialog()}
          title="Neue Wohneinheit anlegen"
        >
          <AddIcon />
        </StyledFab>

        {/* Dialog für Wohneinheit hinzufügen/bearbeiten */}
        <Dialog 
          open={open} 
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <ApartmentIcon sx={{ mr: 2, color: '#1a365d' }} />
              {editMode ? 'Wohneinheit bearbeiten' : 'Neue Wohneinheit anlegen'}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Zugehöriges Objekt</InputLabel>
                  <Select
                    name="objektid"
                    value={formData.objektid}
                    onChange={handleInputChange}
                    label="Zugehöriges Objekt"
                  >
                    {objects.map((object) => (
                      <MenuItem key={object.objektid} value={object.objektid}>
                        <ListItem sx={{ p: 0 }}>
                          <ListItemAvatar>
                            <Avatar sx={{ backgroundColor: '#1a365d', width: 32, height: 32 }}>
                              <BusinessIcon fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={object.objektkuerzel}
                            secondary={`${object.objektstrasse} ${object.objekthausnummer}, ${object.objektort}`}
                          />
                        </ListItem>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Wohneinheit-Nummer"
                  name="wohneinheitnummer"
                  value={formData.wohneinheitnummer}
                  onChange={handleInputChange}
                  required
                  placeholder="z.B. 01, A1, EG-01"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bezeichnung"
                  name="wohneinheitbezeichnung"
                  value={formData.wohneinheitbezeichnung}
                  onChange={handleInputChange}
                  placeholder="z.B. 2-Zimmer-Wohnung"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Größe (m²)"
                  name="wohneinheitgroesse"
                  type="number"
                  value={formData.wohneinheitgroesse}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Typ</InputLabel>
                  <Select
                    name="wohneinheittyp"
                    value={formData.wohneinheittyp}
                    onChange={handleInputChange}
                    label="Typ"
                  >
                    <MenuItem value="Wohnung">Wohnung</MenuItem>
                    <MenuItem value="Gewerbe">Gewerbe</MenuItem>
                    <MenuItem value="Büro">Büro</MenuItem>
                    <MenuItem value="Lager">Lager</MenuItem>
                    <MenuItem value="Garage">Garage</MenuItem>
                    <MenuItem value="Stellplatz">Stellplatz</MenuItem>
                    <MenuItem value="Sonstiges">Sonstiges</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="wohneinheitstatus"
                    value={formData.wohneinheitstatus}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="verfügbar">Verfügbar</MenuItem>
                    <MenuItem value="vermietet">Vermietet</MenuItem>
                    <MenuItem value="renovierung">Renovierung</MenuItem>
                    <MenuItem value="reserviert">Reserviert</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Lage im Objekt (Etage)</InputLabel>
                  <Select
                    name="lage_im_objekt"
                    value={formData.lage_im_objekt || ''}
                    onChange={handleInputChange}
                    label="Lage im Objekt (Etage)"
                  >
                    <MenuItem value="">-- Keine Angabe --</MenuItem>
                    {['KG', 'EG', 'EG links', 'EG rechts', 'EG Mitte',
                      '1. OG', '1. OG links', '1. OG rechts', '1. OG Mitte',
                      '2. OG', '2. OG links', '2. OG rechts', '2. OG Mitte',
                      '3. OG', '3. OG links', '3. OG rechts', '3. OG Mitte',
                      '4. OG', 'DG', 'DG links', 'DG rechts',
                    ].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  name="wohnungsbezeichnung"
                  label="Wohnungsbezeichnung (Detail)"
                  fullWidth
                  value={formData.wohnungsbezeichnung || ''}
                  onChange={handleInputChange}
                  placeholder="z.B. Hinterhaus, Dachgeschoss links"
                  helperText="Erscheint in der Mietbescheinigung"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleCloseDialog}
              startIcon={<CancelIcon />}
              sx={{ color: '#718096' }}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleSaveUnit}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading || !formData.objektid || !formData.wohneinheitnummer}
              sx={{ 
                background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2d3748 0%, #1a365d 100%)',
                }
              }}
            >
              {editMode ? 'Aktualisieren' : 'Anlegen'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar für Benachrichtigungen */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default Wohneinheiten;
